import { useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import { Popconfirm, Space, message } from "antd";
import {
  useTicketTemplatesQuery,
  useTicketTemplateDeleteMutation,
  TICKET_TEMPLATES_QUERY,
} from "@ticketingplatform/api/dist/ticketTemplate";
import { TicketTemplateNode } from "@ticketingplatform/api/dist/ticketTemplate/__generated__/fragments";
import { pageToSkip, renderTableDateColumn } from "utils/tableUtils";
import { ColumnsType } from "antd/es/table";
import { DeleteIcon } from "assets/icons";
import { useVenueContext } from "contexts/VenueContext";
import { useTableQueryParams } from "hooks/useTableQueryParams";
import {
  getColumnTextFilterProps,
  getTextFilterInput,
} from "components/data/TableTextFilter/utils";
import { Link, generatePath } from "react-router-dom";
import { EditOutlined } from "@ant-design/icons";
import { ROUTE_PATHS } from "const/paths";
import { TicketTemplatesVariables } from "@ticketingplatform/api/dist/ticketTemplate/__generated__/queries";

import { UseTicketTemplateListReturnType } from "./types";
import TicketPoolSummaryList from "./partials/TicketPoolSummaryList";

export const useDeleteTicketTemplate = () => {
  const intl = useIntl();
  const [mutation, result] = useTicketTemplateDeleteMutation({
    evictQueries: [TICKET_TEMPLATES_QUERY],
    onCompleted: () =>
      message.success(
        intl.formatMessage({
          defaultMessage: "Ticket Template has been removed!",
        })
      ),
    onError: () =>
      message.error(
        intl.formatMessage({
          defaultMessage: "Cannot remove the Ticket Template",
        })
      ),
  });

  const deleteTicketTemplate = useCallback(
    (id: number) => mutation({ variables: { id } }),
    [mutation]
  );

  return [deleteTicketTemplate, result] as const;
};

export const useTicketTemplateColumns = () => {
  const intl = useIntl();
  const [deleteTicketTemplate] = useDeleteTicketTemplate();
  const [{ sortOrder, filters }] = useTableQueryParams<TicketTemplateNode>();

  return useMemo<ColumnsType<TicketTemplateNode>>(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Template name",
        }),
        key: "name",
        dataIndex: "name",
        sorter: {
          multiple: 1,
        },
        sortOrder: sortOrder.name,
        ...getColumnTextFilterProps("name", filters?.name),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Ticket Types / Prices / Fee / QTY",
        }),
        key: "ticketPools",
        render(_, { ticketPools }) {
          const { nodes: items, count } = ticketPools || {};

          return <TicketPoolSummaryList {...{ items, count }} />;
        },
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Created",
        }),
        key: "createdAt",
        dataIndex: "createdAt",
        sorter: {
          multiple: 2,
        },
        sortOrder: sortOrder.createdAt,
        render: renderTableDateColumn(),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Action",
        }),
        key: "action",
        render: (_, { id, name }) => (
          <Space size="middle">
            <Link
              to={generatePath(ROUTE_PATHS.TicketTemplateEdit, {
                id: id.toString(),
              })}
            >
              <EditOutlined />
            </Link>
            <Popconfirm
              title={intl.formatMessage(
                {
                  defaultMessage:
                    "Are you sure you want to delete Ticket Template: {name}?",
                },
                { name }
              )}
              onConfirm={() => deleteTicketTemplate(id)}
            >
              <DeleteIcon />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteTicketTemplate, intl, sortOrder, filters]
  );
};

export const useTicketTemplateList = (): UseTicketTemplateListReturnType => {
  const { venue } = useVenueContext();
  const [{ page, pageSize, orderBy, filters }, setTableQueryParams] =
    useTableQueryParams<TicketTemplateNode>();

  const variables = useMemo<TicketTemplatesVariables>(
    () => ({
      take: pageSize,
      skip: pageToSkip(page, pageSize),
      filters: {
        ...(venue ? { venueId: { equals: venue.id } } : {}),
        ...getTextFilterInput(filters?.name, "name"),
      },
      orderBy,
      ticketPoolsTake: 3,
    }),
    [filters?.name, orderBy, page, pageSize, venue]
  );

  const { data, loading } = useTicketTemplatesQuery({ variables });

  const onChange = useCallback<UseTicketTemplateListReturnType["onChange"]>(
    ({ current, pageSize: newPageSize }, newFilters, sorter) => {
      setTableQueryParams({
        page: current,
        pageSize: newPageSize,
        sorter,
        filters: newFilters,
      });
    },
    [setTableQueryParams]
  );

  const { nodes: dataSource, count: total } = data?.ticketTemplates || {};

  return useMemo(
    () => ({
      loading,
      pagination: {
        pageSize,
        current: page,
        total,
      },
      dataSource,
      onChange,
    }),
    [dataSource, loading, onChange, page, pageSize, total]
  );
};
